import {
  getAllExporters,
  updateExporter,
  createExporter,
  deleteExporter,
  uploadExporterLogo,
} from 'common/src/api/exporters';
import { SEASONS } from 'common/src/utils/definitions';
import { Exporter, Species } from 'common/src/utils/types';
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Image,
  Switch,
  Card,
  CardBody,
  Skeleton,
  Chip,
  Select,
  SelectItem,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import Header from '../../../components/layouts/Header';
import { ConfirmModal, PageLayout } from '../../../components/ui';
import { Icon } from '@iconify/react';
import ExporterDivisionsEditor from './ExporterDivisionsEditor';
import {
  getAllSpecies,
  getExporterSpecies,
  updateExporterSpecies,
} from 'common/src/api/species';

const FruticSeasonsSelect = ({
  value,
  onChange,
}: {
  value: string[];
  onChange: (seasons: string[]) => void;
}) => (
  <Select
    label='Temporadas con Frutic'
    description='Sin selección, Frutic aplica a todas las temporadas'
    selectionMode='multiple'
    selectedKeys={new Set(value)}
    onSelectionChange={(keys) => onChange(Array.from(keys as Set<string>))}
  >
    {Object.values(SEASONS).map((season) => (
      <SelectItem key={season.name}>{season.name}</SelectItem>
    ))}
  </Select>
);

const LogoUploader = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (url: string) => void;
}) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('El archivo debe ser una imagen');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('La imagen no puede superar los 20MB');
      return;
    }

    setUploading(true);
    try {
      onChange(await uploadExporterLogo(file));
      toast.success('Logo subido correctamente');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('No se pudo subir el logo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className='flex flex-col gap-2 py-2'>
      <div className='flex items-center gap-3'>
        {value ? (
          <Image
            src={value}
            alt='Logo'
            width={56}
            height={56}
            className='rounded-full object-cover'
          />
        ) : (
          <div className='w-14 h-14 rounded-full border border-dashed border-default-300 bg-default-50' />
        )}
        <div className='flex flex-col gap-1'>
          <span className='text-sm font-medium'>Logo</span>
          <div className='flex items-center gap-2'>
            <Button
              size='sm'
              variant='flat'
              isLoading={uploading}
              onPress={() => inputRef.current?.click()}
            >
              {value ? 'Cambiar imagen' : 'Subir imagen'}
            </Button>
            {value && (
              <Button
                size='sm'
                variant='light'
                color='danger'
                onPress={() => onChange('')}
              >
                Quitar
              </Button>
            )}
          </div>
        </div>
      </div>
      <Input
        label='Logo URL'
        placeholder='O pega una URL directa'
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        className='hidden'
        onChange={handleFileChange}
      />
    </div>
  );
};

const VOLUME_ACCESS = [
  { key: 'none', label: 'Sin acceso', color: 'default', description: 'No ve Volumen' },
  { key: 'preview', label: 'Preview', color: 'warning', description: 'Solo los totales de la industria' },
  { key: 'full', label: 'Suscrita', color: 'success', description: 'Suscrita a ASOEX: ve todo' },
] as const;

const SuperadminExporters = () => {
  const [exporters, setExporters] = useState<Exporter[]>([]);
  const [loading, setLoading] = useState(true);
  const [allSpecies, setAllSpecies] = useState<Species[]>([]);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [selectedExporter, setSelectedExporter] = useState<Exporter | null>(
    null
  );
  const [selectedSpecies, setSelectedSpecies] = useState<string[]>([]);
  const [newExporterSpecies, setNewExporterSpecies] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [newExporter, setNewExporter] = useState<{
    name: string;
    excel_name: string;
    logo_url: string;
    hasSettlements: boolean;
    hasDemo: boolean;
    hasFrutic: boolean;
    frutic_seasons: string[];
    email_domain: string;
    isActive: boolean;
  }>({
    name: '',
    excel_name: '',
    logo_url: '',
    hasSettlements: false,
    hasDemo: false,
    hasFrutic: false,
    frutic_seasons: [],
    email_domain: '',
    isActive: true,
  });
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isNewOpen,
    onOpen: onNewOpen,
    onClose: onNewClose,
  } = useDisclosure();

  const fetchExporters = async () => {
    setLoading(true);
    try {
      const fetchedExporters = await getAllExporters();
      setExporters(fetchedExporters);
    } catch (error) {
      console.error('Error fetching exporters:', error);
      toast.error('Failed to fetch exporters');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSpecies = async () => {
    setLoadingSpecies(true);
    try {
      const fetchedSpecies = await getAllSpecies();
      setAllSpecies(fetchedSpecies);
    } catch (error) {
      console.error('Error fetching species:', error);
      toast.error('Failed to fetch species');
    } finally {
      setLoadingSpecies(false);
    }
  };

  useEffect(() => {
    fetchExporters();
    fetchAllSpecies();
  }, []);

  const handleEdit = async (exporter: Exporter) => {
    setSelectedExporter(exporter);

    // Set the selected species based on the exporter's species
    if (exporter.species && exporter.species.length > 0) {
      const speciesIds = exporter.species.map((relation) =>
        relation.species_id.toString()
      );
      setSelectedSpecies(speciesIds);

      console.log('speciesIds', speciesIds);
    } else {
      setSelectedSpecies([]);
    }

    onEditOpen();
  };

  const handleDelete = (exporter: Exporter) => {
    setSelectedExporter(exporter);
    onDeleteOpen();
  };

  // Cambio rápido desde la tabla: se ve al tiro y vuelve atrás si falla.
  const changeVolumeAccess = async (
    exporter: Exporter,
    access: NonNullable<Exporter['industry_volume_access']>
  ) => {
    const before = exporter.industry_volume_access ?? 'none';
    if (access === before) return;
    const setAccess = (value: Exporter['industry_volume_access']) =>
      setExporters((prev) =>
        prev.map((e) =>
          e.id === exporter.id ? { ...e, industry_volume_access: value } : e
        )
      );
    setAccess(access);
    try {
      await updateExporter(exporter.id, { industry_volume_access: access });
    } catch (error) {
      setAccess(before);
      toast.error(`No se pudo cambiar el acceso de ${exporter.name}`);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedExporter) return;
    try {
      // Crear una copia del exportador sin la propiedad 'species'
      const { species, ...exporterWithoutSpecies } = selectedExporter;

      // Agregar log para depuración
      console.log(
        'Actualizando exportador sin especies:',
        exporterWithoutSpecies
      );

      // Actualizar el exportador sin enviar la propiedad 'species'
      await updateExporter(selectedExporter.id, exporterWithoutSpecies);

      // Update exporter species
      const speciesIds = selectedSpecies.map((id) => parseInt(id));

      // Agregar log para depuración
      console.log('Actualizando especies del exportador:', speciesIds);

      await updateExporterSpecies(selectedExporter.id, speciesIds);

      toast.success('Exporter updated successfully');
      onEditClose();
      fetchExporters();
    } catch (error) {
      console.error('Error updating exporter:', error);
      toast.error('Failed to update exporter');
    }
  };

  const handleNewSubmit = async () => {
    try {
      // Agregar log para depuración
      console.log('Creando nuevo exportador:', newExporter);

      const created = await createExporter(newExporter);

      // console.log('created', created);

      // Update species for the new exporter
      if (created && created.id) {
        const speciesIds = newExporterSpecies.map((id) => parseInt(id));

        // Agregar log para depuración
        // console.log('Agregando especies al nuevo exportador:', speciesIds);

        await updateExporterSpecies(created.id, speciesIds);
      }

      toast.success('New exporter created successfully');
      onNewClose();
      setNewExporter({
        name: '',
        excel_name: '',
        logo_url: '',
        hasSettlements: false,
        hasDemo: false,
        hasFrutic: false,
        frutic_seasons: [],
        email_domain: '',
      });
      setNewExporterSpecies([]);
      fetchExporters();
    } catch (error) {
      console.error('Error creating exporter:', error);
      toast.error('Failed to create new exporter');
    }
  };

  const handleNewOpen = () => {
    // Asegurar que el ID de especie es un número (especie cherry con ID 2)
    setNewExporterSpecies(['2']);
    onNewOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedExporter) return;
    try {
      await deleteExporter(selectedExporter.id);
      toast.success('Exporter deleted successfully');
      onDeleteClose();
      fetchExporters();
    } catch (error) {
      console.error('Error deleting exporter:', error);
      toast.error('Failed to delete exporter');
    }
  };

  const columns = [
    { name: 'ID', uid: 'id' },
    { name: 'Logo', uid: 'logo' },
    { name: 'Nombre', uid: 'name' },
    { name: 'Nombre en Excel', uid: 'excel_name' },
    { name: 'Dominio de Email', uid: 'email_domain' },
    { name: 'Activo', uid: 'isActive' },
    { name: 'Liquidaciones', uid: 'hasSettlements' },
    { name: 'Demo', uid: 'hasDemo' },
    { name: 'Portal Frutic', uid: 'hasFrutic' },
    { name: 'Volumen ASOEX', uid: 'industry_volume_access' },
    { name: 'Especies', uid: 'species' },
    { name: 'Acciones', uid: 'actions' },
  ];

  const renderCell = (exporter: Exporter, columnKey: React.Key) => {
    console.log('exporter', exporter);
    switch (columnKey) {
      case 'logo':
        return (
          <Image
            src={exporter.logo_url}
            alt={`${exporter.name} logo`}
            width={40}
            height={40}
            className='rounded-full'
          />
        );
      case 'actions':
        return (
          <div className='flex items-center gap-2'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              onClick={() => handleEdit(exporter)}
            >
              <Icon icon='tabler:edit' className='text-default-400' />
            </Button>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              color='danger'
              onClick={() => handleDelete(exporter)}
            >
              <Icon icon='tabler:trash' className='text-danger-400' />
            </Button>
          </div>
        );
      case 'hasSettlements':
        return (
          <div className=''>
            <Chip
              color={exporter.hasSettlements ? 'success' : 'danger'}
              size='sm'
              variant='flat'
            >
              {exporter.hasSettlements ? 'Yes' : 'No'}
            </Chip>
          </div>
        );
      case 'isActive':
        return (
          <div className=''>
            <Chip
              color={exporter.isActive !== false ? 'success' : 'danger'}
              size='sm'
              variant='flat'
            >
              {exporter.isActive !== false ? 'Activo' : 'Inactivo'}
            </Chip>
          </div>
        );
      case 'hasDemo':
        return (
          <div className=''>
            <Chip
              color={exporter.hasDemo ? 'success' : 'danger'}
              size='sm'
              variant='flat'
            >
              {exporter.hasDemo ? 'Yes' : 'No'}
            </Chip>
          </div>
        );
      case 'hasFrutic':
          return (
            <div className=''>
              <Chip
                color={exporter.hasFrutic ? 'success' : 'danger'}
                size='sm'
                variant='flat'
              >
                {exporter.hasFrutic
                  ? exporter.frutic_seasons?.join(', ') || 'Yes'
                  : 'No'}
              </Chip>
            </div>
          );
      case 'industry_volume_access': {
        const access = VOLUME_ACCESS.find(
          (a) => a.key === (exporter.industry_volume_access ?? 'none')
        )!;
        return (
          <Dropdown>
            <DropdownTrigger>
              <Button
                size='sm'
                radius='full'
                variant='flat'
                color={access.color}
                endContent={<Icon icon='tabler:chevron-down' width={14} />}
                aria-label={`Volumen ASOEX de ${exporter.name}: ${access.label}`}
              >
                {access.label}
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label='Acceso a Volumen ASOEX'
              selectionMode='single'
              disallowEmptySelection
              selectedKeys={[access.key]}
              onAction={(key) =>
                changeVolumeAccess(
                  exporter,
                  key as NonNullable<Exporter['industry_volume_access']>
                )
              }
            >
              {VOLUME_ACCESS.map((a) => (
                <DropdownItem key={a.key} description={a.description}>
                  {a.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        );
      }
      case 'species':
        return (
          <div className='flex flex-wrap gap-1'>
            {exporter.species && exporter.species.length > 0 ? (
              exporter.species.map((speciesRelation) => (
                <Chip
                  key={speciesRelation.id}
                  size='sm'
                  variant='flat'
                  className='mr-1 mb-1'
                >
                  {speciesRelation.species?.name ||
                    `ID: ${speciesRelation.species_id}`}
                </Chip>
              ))
            ) : (
              <Chip size='sm' variant='flat' color='warning'>
                No especies
              </Chip>
            )}
          </div>
        );
      default:
        return String(exporter[columnKey as keyof Exporter] || '');
    }
  };

  const filteredExporters = exporters
    .filter((exporter) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        !searchQuery ||
        exporter.name?.toLowerCase().includes(searchLower) ||
        exporter.excel_name?.toLowerCase().includes(searchLower) ||
        exporter.email_domain?.toLowerCase().includes(searchLower) ||
        exporter.species?.some((s) =>
          s.species?.name?.toLowerCase().includes(searchLower)
        )
      );
    })
    .sort((a, b) => {
      // Sort alphabetically by name
      return (a.name || '').localeCompare(b.name || '');
    });

  return (
    <PageLayout>
      <Header
        title='Exportadoras'
        actions={
          <Button
            color='primary'
            onClick={handleNewOpen}
          >
            Añadir Exportadora
          </Button>
        }
      />
      <div className='mb-4'>
        <Input
          placeholder='Buscar por nombre, nombre en Excel, dominio de email o especie...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='max-w-xl'
          isClearable
          onClear={() => setSearchQuery('')}
        />
      </div>

      {loading ? (
        <div className=''>
          {/* Title and Button Skeleton */}
          <div className='flex justify-between items-center '>
            <div className='space-y-2'>
              <Skeleton className='w-1/3 rounded-lg' />
            </div>
          </div>

          {/* Table Skeleton */}
          <Card className='w-full'>
            <CardBody>
              <div className='space-y-4'>
                {/* Header Row */}
                <div className='grid grid-cols-9 gap-4'>
                  {[...Array(9)].map((_, i) => (
                    <Skeleton key={i} className='h-8 w-full rounded-lg' />
                  ))}
                </div>

                {/* Table Rows */}
                {[...Array(5)].map((_, rowIndex) => (
                  <div key={rowIndex} className='grid grid-cols-9 gap-4'>
                    {/* ID */}
                    <Skeleton className='h-12 w-16 rounded-lg' />
                    {/* Logo */}
                    <Skeleton className='h-12 w-12 rounded-full' />
                    {/* Name */}
                    <Skeleton className='h-12 w-full rounded-lg' />
                    {/* Excel Name */}
                    <Skeleton className='h-12 w-full rounded-lg' />
                    {/* Email Domain */}
                    <Skeleton className='h-12 w-24 rounded-lg mx-auto' />
                    {/* Liquidaciones */}
                    <Skeleton className='h-12 w-24 rounded-lg mx-auto' />
                    {/* Demo */}
                    <Skeleton className='h-12 w-24 rounded-lg mx-auto' />
                    {/* Species */}
                    <Skeleton className='h-12 w-full rounded-lg' />
                    {/* Actions */}
                    <div className='flex gap-2 justify-center'>
                      <Skeleton className='h-8 w-8 rounded-lg' />
                      <Skeleton className='h-8 w-8 rounded-lg' />
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      ) : (
        <Table removeWrapper aria-label='Exporters table'>
          <TableHeader columns={columns}>
            {(column) => (
              <TableColumn key={column.uid}>{column.name}</TableColumn>
            )}
          </TableHeader>
          <TableBody items={filteredExporters}>
            {(item) => (
              <TableRow key={item.id}>
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Modal isOpen={isEditOpen} onClose={onEditClose} size='lg'>
        <ModalContent>
          <ModalHeader>Edit Exporter</ModalHeader>
          <ModalBody>
            <Input
              label='Name'
              value={selectedExporter?.name || ''}
              onChange={(e) =>
                setSelectedExporter({
                  ...selectedExporter!,
                  name: e.target.value,
                })
              }
            />
            <Input
              label='Excel Name'
              value={selectedExporter?.excel_name || ''}
              onChange={(e) =>
                setSelectedExporter({
                  ...selectedExporter!,
                  excel_name: e.target.value,
                })
              }
            />
            <LogoUploader
              value={selectedExporter?.logo_url || ''}
              onChange={(url) =>
                setSelectedExporter({
                  ...selectedExporter!,
                  logo_url: url,
                })
              }
            />
            <Input
              label='Email Domain'
              value={selectedExporter?.email_domain || ''}
              onChange={(e) =>
                setSelectedExporter({
                  ...selectedExporter!,
                  email_domain: e.target.value,
                })
              }
            />
            <div className='flex items-center gap-2 py-2'>
              <Switch
                isSelected={selectedExporter?.isActive !== false}
                onValueChange={(checked) =>
                  setSelectedExporter({
                    ...selectedExporter!,
                    isActive: checked,
                  })
                }
              >
                Activo
              </Switch>
            </div>
            <div className='flex items-center gap-2 py-2'>
              <Switch
                isSelected={selectedExporter?.hasSettlements || false}
                onValueChange={(checked) =>
                  setSelectedExporter({
                    ...selectedExporter!,
                    hasSettlements: checked,
                  })
                }
              >
                Sección de Liquidaciones
              </Switch>
            </div>
            <div className='flex items-center gap-2 py-2'>
              <Switch
                isSelected={selectedExporter?.hasFrutic || false}
                onValueChange={(checked) =>
                  setSelectedExporter({
                    ...selectedExporter!,
                    hasFrutic: checked,
                  })
                }
              >
                Integración con Frutic
              </Switch>
            </div>
            {selectedExporter?.hasFrutic && (
              <FruticSeasonsSelect
                value={selectedExporter.frutic_seasons || []}
                onChange={(seasons) =>
                  setSelectedExporter({
                    ...selectedExporter,
                    frutic_seasons: seasons,
                  })
                }
              />
            )}
            <div className='flex items-center gap-2 py-2'>
              <Switch
                isSelected={selectedExporter?.hasDemo || false}
                onValueChange={(checked) =>
                  setSelectedExporter({
                    ...selectedExporter!,
                    hasDemo: checked,
                  })
                }
              >
                Demo
              </Switch>
            </div>
            {/* Base de volumen de ASOEX: completa solo para suscritas. */}
            <Select
              label='Volumen ASOEX'
              selectedKeys={[selectedExporter?.industry_volume_access || 'none']}
              disallowEmptySelection
              onSelectionChange={(keys) =>
                setSelectedExporter({
                  ...selectedExporter!,
                  industry_volume_access: (Array.from(keys)[0] ||
                    'none') as Exporter['industry_volume_access'],
                })
              }
              description='Suscrita ve todo Volumen; Preview, solo los totales de la industria.'
            >
              <SelectItem key='none'>Sin acceso</SelectItem>
              <SelectItem key='preview'>Preview (sin ASOEX)</SelectItem>
              <SelectItem key='full'>Suscrita a ASOEX</SelectItem>
            </Select>
            <div className='flex items-center gap-2 py-2'>
              <Switch
                isSelected={
                  selectedExporter?.custom_cost_categories_enabled || false
                }
                onValueChange={(checked) =>
                  setSelectedExporter({
                    ...selectedExporter!,
                    custom_cost_categories_enabled: checked,
                  })
                }
              >
                Categorías de costos propias
              </Switch>
            </div>
            {selectedExporter?.id ? (
              <ExporterDivisionsEditor exporterId={selectedExporter.id} />
            ) : null}
            <div className='mt-4'>
              <h3 className='text-sm font-medium mb-2'>Especies:</h3>
              <div className='grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto p-2 border border-default-200 rounded-lg'>
                {allSpecies.map((species) => (
                  <div key={species.id} className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      id={`edit-species-${species.id}`}
                      checked={selectedSpecies.includes(species.id.toString())}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSpecies([
                            ...selectedSpecies,
                            species.id.toString(),
                          ]);
                        } else {
                          setSelectedSpecies(
                            selectedSpecies.filter(
                              (id) => id !== species.id.toString()
                            )
                          );
                        }
                      }}
                      className='h-4 w-4 rounded border-default-300'
                    />
                    <label
                      htmlFor={`edit-species-${species.id}`}
                      className='text-sm text-default-700'
                    >
                      {species.name} ({species.excel_name})
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onClick={onEditClose}
            >
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={handleEditSubmit}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title='Confirm Delete'
        confirmLabel='Delete'
        cancelLabel='Cancel'
        isDestructive
      >
        Are you sure you want to delete {selectedExporter?.name}?
      </ConfirmModal>

      <Modal isOpen={isNewOpen} onClose={onNewClose} size='lg'>
        <ModalContent>
          <ModalHeader>New Exporter</ModalHeader>
          <ModalBody>
            <Input
              label='Name'
              value={newExporter.name}
              onChange={(e) =>
                setNewExporter({ ...newExporter, name: e.target.value })
              }
            />
            <Input
              label='Excel Name'
              value={newExporter.excel_name}
              onChange={(e) =>
                setNewExporter({ ...newExporter, excel_name: e.target.value })
              }
            />
            <LogoUploader
              value={newExporter.logo_url}
              onChange={(url) => setNewExporter({ ...newExporter, logo_url: url })}
            />
            <Input
              label='Email Domain'
              value={newExporter.email_domain}
              onChange={(e) =>
                setNewExporter({ ...newExporter, email_domain: e.target.value })
              }
            />
            <div className='flex items-center gap-2 py-2'>
              <Switch
                isSelected={newExporter.isActive}
                onValueChange={(checked) =>
                  setNewExporter({ ...newExporter, isActive: checked })
                }
              >
                Activo
              </Switch>
            </div>
            <div className='flex items-center gap-2 py-2'>
              <Switch
                isSelected={newExporter.hasSettlements}
                onValueChange={(checked) =>
                  setNewExporter({ ...newExporter, hasSettlements: checked })
                }
              >
                Sección de Liquidaciones
              </Switch>
            </div>
            <div className='flex items-center gap-2 py-2'>
              <Switch
                isSelected={newExporter.hasFrutic}
                onValueChange={(checked) =>
                  setNewExporter({ ...newExporter, hasFrutic: checked })
                }
              >
                Integración con Frutic
              </Switch>
            </div>
            {newExporter.hasFrutic && (
              <FruticSeasonsSelect
                value={newExporter.frutic_seasons}
                onChange={(seasons) =>
                  setNewExporter({ ...newExporter, frutic_seasons: seasons })
                }
              />
            )}
            <div className='flex items-center gap-2 py-2'>
              <Switch
                isSelected={newExporter.hasDemo}
                onValueChange={(checked) =>
                  setNewExporter({ ...newExporter, hasDemo: checked })
                }
              >
                Demo
              </Switch>
            </div>
            <div className='mt-4'>
              <h3 className='text-sm font-medium mb-2'>Especies:</h3>
              <div className='grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto p-2 border border-default-200 rounded-lg'>
                {allSpecies.map((species) => (
                  <div key={species.id} className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      id={`new-species-${species.id}`}
                      checked={newExporterSpecies.includes(
                        species.id.toString()
                      )}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewExporterSpecies([
                            ...newExporterSpecies,
                            species.id.toString(),
                          ]);
                        } else {
                          setNewExporterSpecies(
                            newExporterSpecies.filter(
                              (id) => id !== species.id.toString()
                            )
                          );
                        }
                      }}
                      className='h-4 w-4 rounded border-default-300'
                    />
                    <label
                      htmlFor={`new-species-${species.id}`}
                      className='text-sm text-default-700'
                    >
                      {species.name} ({species.excel_name})
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onClick={onNewClose}
            >
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={handleNewSubmit}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminExporters;
